import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Settings, Lock, LogOut, ChevronDown } from 'lucide-react';

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'process_manager': return 'Admin';
      case 'accountant': return 'Accountant';
      case 'case_officer': return 'Case Officer';
      case 'client': return 'Client';
      default: return role;
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
        data-testid="profile-menu-button"
      >
        <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-white font-semibold text-sm">
          {getInitials(user?.full_name)}
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-slate-900 leading-tight">{user?.full_name}</p>
          <p className="text-xs text-slate-500">{getRoleDisplay(user?.role)}</p>
        </div>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50" data-testid="profile-dropdown">
          {/* User Info Header */}
          <div className="p-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold">
                {getInitials(user?.full_name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-2">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/profile');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
              data-testid="profile-menu-profile"
            >
              <User size={18} className="text-slate-600" />
              <span className="font-medium">My Profile</span>
            </button>
            
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/change-password');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
              data-testid="profile-menu-password"
            >
              <Lock size={18} className="text-slate-600" />
              <span className="font-medium">Change Password</span>
            </button>
            
            {user?.role === 'super_admin' && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin/settings');
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
                data-testid="profile-menu-settings"
              >
                <Settings size={18} className="text-slate-600" />
                <span className="font-medium">Settings</span>
              </button>
            )}
          </div>

          {/* Logout */}
          <div className="p-2 border-t border-slate-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-red-600 hover:bg-red-50 rounded-lg transition-all"
              data-testid="profile-menu-logout"
            >
              <LogOut size={18} />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
